// src/contexts/ViewContext.tsx
import * as React from 'react';
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ViewMode, ChartType, SavedView, UserPreferences } from '../types/ViewTypes';
import { DataFilters } from '../types/FilterTypes';

interface ViewContextType {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  chartType: ChartType;
  setChartType: (type: ChartType) => void;
  savedViews: SavedView[];
  saveView: (name: string, description?: string) => void;
  loadView: (id: string) => void;
  deleteView: (id: string) => void;
  currentViewName: string | null;
  userPreferences: UserPreferences;
  updatePreferences: (updates: Partial<UserPreferences>) => void;
  filters: DataFilters;
  setFilters: (filters: DataFilters) => void;
  updateFilter: <K extends keyof DataFilters>(key: K, value: DataFilters[K]) => void;
  resetFilters: () => void;
  shareUrl: string | null;
  generateShareUrl: (includeFilters?: boolean) => string;
}

// Default user preferences
const defaultPreferences: UserPreferences = {
  theme: 'system',
  defaultViewMode: ViewMode.MAP,
  defaultChartType: ChartType.LINE,
  defaultColorScheme: 'viridis',
  showLegend: true,
  showGrid: true,
  animateTransitions: true
};

// Create the context with undefined initial value
export const ViewContext = createContext<ViewContextType | undefined>(undefined);

export const ViewProvider: React.FC<{ 
  children: ReactNode;
  initialViewMode?: ViewMode;
  initialChartType?: ChartType;
}> = ({ 
  children, 
  initialViewMode = ViewMode.MAP,
  initialChartType = ChartType.LINE
}) => {
  // State for view mode, chart type, etc.
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode);
  const [chartType, setChartType] = useState<ChartType>(initialChartType);
  const [savedViews, setSavedViews] = useState<SavedView[]>([]);
  const [currentViewName, setCurrentViewName] = useState<string | null>(null);
  const [userPreferences, setUserPreferences] = useState<UserPreferences>(defaultPreferences);
  const [filters, setFilters] = useState<DataFilters>({});
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  // Save view to local storage
  const saveView = (name: string, description?: string) => {
    const newView: SavedView = {
      id: Date.now().toString(),
      name,
      description,
      viewMode,
      chartType,
      filters,
      preferences: userPreferences,
      createdAt: new Date().toISOString()
    };
    setSavedViews(prev => [...prev, newView]);
    setCurrentViewName(name);
  };

  // Load view from local storage
  const loadView = (id: string) => {
    const view = savedViews.find(v => v.id === id);
    if (view) {
      setViewMode(view.viewMode);
      setChartType(view.chartType);
      setFilters(view.filters);
      setUserPreferences(view.preferences);
      setCurrentViewName(view.name);
    }
  };

  // Delete view from local storage
  const deleteView = (id: string) => {
    setSavedViews(prev => {
      const updatedViews = prev.filter(v => v.id !== id);
      // Check if the deleted view was the current view using the previous state
      const deletedView = prev.find(v => v.id === id);
      if (deletedView?.name === currentViewName) {
        setCurrentViewName(null);
      }
      return updatedViews;
    });
  };

  // Update user preferences
  const updatePreferences = (updates: Partial<UserPreferences>) => {
    setUserPreferences(prev => ({ ...prev, ...updates }));
  };

  // Update a single filter
  const updateFilter = <K extends keyof DataFilters>(key: K, value: DataFilters[K]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({});
  };

  // Generate shareable URL
  const generateShareUrl = (includeFilters = true) => {
    const url = new URL(window.location.href);
    url.searchParams.set('mode', viewMode);
    url.searchParams.set('chart', chartType);
    
    if (includeFilters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          url.searchParams.set(key, value.join(','));
        } else {
          url.searchParams.set(key, String(value));
        }
      });
    }
    
    const shareUrl = url.toString();
    setShareUrl(shareUrl);
    return shareUrl;
  };

  // Load view state from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlViewMode = params.get('mode');
    const urlChartType = params.get('chart');
    
    // Validate view mode parameter
    if (urlViewMode && Object.values(ViewMode).includes(urlViewMode as ViewMode)) {
      setViewMode(urlViewMode as ViewMode);
    }
    
    // Validate chart type parameter
    if (urlChartType && Object.values(ChartType).includes(urlChartType as ChartType)) {
      setChartType(urlChartType as ChartType);
    }
    
    const urlFilters: DataFilters = {};
    params.forEach((value, key) => {
      if (key !== 'mode' && key !== 'chart') {
        urlFilters[key] = value.includes(',') ? value.split(',') : value;
      }
    });
    
    if (Object.keys(urlFilters).length > 0) {
      setFilters(urlFilters);
    }
  }, []);

  return (
    <ViewContext.Provider 
      value={{
        viewMode,
        setViewMode,
        chartType,
        setChartType,
        savedViews,
        saveView,
        loadView,
        deleteView,
        currentViewName,
        userPreferences,
        updatePreferences,
        filters,
        setFilters,
        updateFilter,
        resetFilters,
        shareUrl,
        generateShareUrl
      }}
    >
      {children}
    </ViewContext.Provider>
  );
};

// Custom hook to use the view context
export const useView = () => {
  const context = useContext(ViewContext);
  if (context === undefined) {
    throw new Error('useView must be used within a ViewProvider');
  }
  return context;
};